'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle, Loader2, Zap, MapPin, Target, TrendingUp } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function WelcomeHero() {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  const handleConnect = () => {
    setConnecting(true);
    // Backend: initiate Strava OAuth flow — redirect to Strava auth URL
    setTimeout(() => {
      setConnecting(false);
      setConnected(true);
      setTimeout(() => {
        window.location.href = '/segment-discovery';
      }, 1200);
    }, 2000);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 lg:px-16">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2 mb-10">
        <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center">
          <img src="/assets/images/ChatGPT_Image_Apr_28__2026__04_10_03_PM-1777385431082.png" alt="KOM Gatherer logo" className="w-full h-full object-contain" />
        </div>
        <span className="font-bold text-xl text-[hsl(var(--foreground))] tracking-tight">KOM Gatherer</span>
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <h2 className="text-2xl font-extrabold text-[hsl(var(--foreground))] leading-tight">
            Start hunting KOMs
          </h2>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            Connect your Strava account to import your activity data. We&apos;ll calculate your fitness level and find segments where you can win.
          </p>
        </div>

        {/* Connect button */}
        <button
          onClick={handleConnect}
          disabled={connecting || connected}
          className={`w-full flex items-center justify-center gap-3 py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-95 ${
            connected
              ? 'bg-green-500 text-white cursor-default' :'bg-[#FC4C02] hover:bg-[#e84400] text-white shadow-lg shadow-[#FC4C02]/25 hover:shadow-[#FC4C02]/40'
          } disabled:opacity-80`}
        >
          {connecting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Connecting to Strava...</span>
            </>
          ) : connected ? (
            <>
              <CheckCircle size={18} />
              <span>Connected! Redirecting...</span>
            </>
          ) : (
            <>
              {/* Strava logo mark */}
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
              </svg>
              <span>Connect with Strava</span>
            </>
          )}
        </button>

        <p className="mt-3 text-center text-[11px] text-[hsl(var(--muted-foreground))]">
          We only read your activity data. We never post on your behalf.
        </p>

        {/* Divider */}
        <div className="my-8 flex items-center gap-3">
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
          <span className="text-xs text-[hsl(var(--muted-foreground))]">Already have an account?</span>
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
        </div>

        {/* Sign in link */}
        <Link
          href="/segment-discovery"
          className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium text-sm border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
        >
          Sign in to your account
        </Link>

        {/* Feature pills — mobile only */}
        <div className="lg:hidden mt-8 grid grid-cols-2 gap-2">
          {[
            { icon: MapPin, text: 'Segments near you' },
            { icon: Target, text: 'Win probability' },
            { icon: TrendingUp, text: 'Fitness analysis' },
            { icon: Zap, text: 'Auto-refresh data' },
          ]?.map((f) => {
            const Icon = f?.icon;
            return (
              <div key={`pill-${f?.text}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-[hsl(var(--muted))]">
                <Icon size={13} className="text-[#FC4C02] shrink-0" />
                <span className="text-[11px] font-medium text-[hsl(var(--foreground))]">{f?.text}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-auto pt-8 text-[11px] text-[hsl(var(--muted-foreground))] text-center">
        By connecting, you agree to our{' '}
        <a href="#" className="underline hover:text-[hsl(var(--foreground))]">Terms of Service</a>
        {' '}and{' '}
        <a href="#" className="underline hover:text-[hsl(var(--foreground))]">Privacy Policy</a>
      </p>
    </div>
  );
}